// [claude-code 2026-03-16] Global error event bus — bridges apiClient errors to React toast system
// [claude-code 2026-03-22] Wire emitApiError → error log ring buffer for persistent error history

import { pushError } from "./errorLog";
import {
  AUTH_SESSION_RECONNECT_MESSAGE,
  BACKEND_MAINTENANCE_MESSAGE,
} from "./maintenance-message";

export interface ApiErrorEvent {
  code: string;
  message: string;
  status?: number;
  endpoint?: string;
  stack?: string;
}

type ErrorListener = (error: ApiErrorEvent) => void;
type RecoveryListener = (endpoint: string) => void;

const listeners = new Set<ErrorListener>();
const recoveryListeners = new Set<RecoveryListener>();

/** Subscribe to API errors. Returns unsubscribe function. */
export function onApiError(listener: ErrorListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function onApiRecovery(listener: RecoveryListener): () => void {
  recoveryListeners.add(listener);
  return () => recoveryListeners.delete(listener);
}

export function emitApiRecovery(endpoint: string): void {
  recoveryListeners.forEach((listener) => listener(endpoint));
}

/** Emit an API error to all listeners + persist to error log */
export function emitApiError(error: ApiErrorEvent): void {
  listeners.forEach((fn) => fn(error));

  const message = error.message || BACKEND_MAINTENANCE_MESSAGE;
  const fix = getFixDescription(error.code, error.status);

  pushError({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    code: error.code,
    message,
    status: error.status,
    endpoint: error.endpoint,
    fix,
    stack: error.stack,
  });
}

/* ------------------------------------------------------------------ */
/*  Fix descriptions — short user-facing hints per error type          */
/* ------------------------------------------------------------------ */

/** Get a short fix description for a given error code / status */
export function getFixDescription(code: string, status?: number): string {
  if (status === 401 || code === "unauthenticated" || code === "auth_skipped") {
    return AUTH_SESSION_RECONNECT_MESSAGE;
  }
  return BACKEND_MAINTENANCE_MESSAGE;
}
